// src/layout/builders/ERP/WeeklyUpdateInchargePanel.jsx
// Incharge: create, upload & submit weekly project updates
// Weeks start on Saturday. Only Saturdays can submit.
import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-hot-toast";
import useAuthStore from "../../../store/useAuthStore";
import { axiosInstancev1 } from "../../../lib/axiosInstance";
import {
  Upload, Send, Image as ImageIcon,
  Home, Building2, MessageSquare, RefreshCw,
  Plus, Trash2, AlertCircle, Calendar, ChevronDown,
} from "lucide-react";
import WeeklyUpdateSidebarView from "./WeeklyUpdateSidebarView";

// Get last Saturday on or before a given date
const getLastSaturday = (date = new Date()) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay(); // 0=Sun, 6=Sat
  const diff = day >= 6 ? 0 : day + 1; // days back to Saturday
  d.setDate(d.getDate() - diff);
  return d.toISOString().split("T")[0];
};

const isSaturday = (date = new Date()) => new Date(date).getDay() === 6;

// Get the last N Saturdays (not including today if today is not Saturday)
const getPastSaturdays = (n = 8) => {
  const saturdays = [];
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  // Walk back until we get n Saturdays
  for (let i = 0; i < 60 && saturdays.length < n; i++) {
    if (d.getDay() === 6) {
      saturdays.push(d.toISOString().split("T")[0]);
    }
    d.setDate(d.getDate() - 1);
  }
  return saturdays;
};

const getWeekRange = (saturdayStr) => {
  if (!saturdayStr) return { start: "", end: "" };
  const start = new Date(saturdayStr);
  const end = new Date(start);
  end.setDate(end.getDate() + 6); // Saturday to Friday
  const fmt = (d) => d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  return { start: fmt(start), end: fmt(end) };
};

const PhotoSlot = ({ label, preview, onChange, disabled = false }) => (
  <label className={`block group ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}>
    <span className="text-xs font-bold text-gray-600 mb-1.5 block">{label}</span>
    <div className={`relative w-24 h-24 sm:w-28 sm:h-28 rounded-xl border-2 border-dashed ${disabled ? "border-gray-100 bg-gray-50" : "border-gray-200 bg-gray-50 hover:border-blue-300"} overflow-hidden transition-colors`}>
      {preview ? (
        <img src={preview} alt={label} className="w-full h-full object-cover" />
      ) : (
        <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-1 p-2">
          <Upload size={18} />
          <span className="text-[9px] font-medium text-center">Upload</span>
        </div>
      )}
      <input type="file" accept="image/*" className="hidden" onChange={onChange} disabled={disabled} />
    </div>
  </label>
);

const WeeklyUpdateInchargePanel = ({ projectId }) => {
  const { user } = useAuthStore();
  const builderId = user?.builderId || user?.builder_id || user?.id;

  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedUpdateId, setSelectedUpdateId] = useState(null);

  // Determine current week (always starts Saturday)
  const todayIsSaturday = isSaturday();
  const currentSaturday = getLastSaturday();

  // Active week to fill: either current Saturday (if today is Sat) or selected missed week
  const [activeWeekStart, setActiveWeekStart] = useState(currentSaturday);
  const [isMissedWeekMode, setIsMissedWeekMode] = useState(false);
  const [showMissedWeekDropdown, setShowMissedWeekDropdown] = useState(false);

  // Form state
  const [comments, setComments] = useState([""]);
  const [files, setFiles] = useState({
    interior_photo_1: null,
    interior_photo_2: null,
    exterior_photo_1: null,
    exterior_photo_2: null,
  });
  const [previews, setPreviews] = useState({});

  const loadUpdates = useCallback(async () => {
    if (!projectId || !builderId) return;
    setLoading(true);
    try {
      const res = await axiosInstancev1.get(
        `/erp/weekly-updates/project/${projectId}?builderId=${builderId}`
      );
      setUpdates(res.data?.data || []);
      setSelectedUpdateId(null);
    } catch (e) {
      const msg = e.response?.data?.message;
      if (e.response?.status === 403) {
        toast.error(msg || "You do not have access to weekly updates for this project");
      } else {
        toast.error(msg || "Failed to load weekly updates");
      }
    }
    setLoading(false);
  }, [projectId, builderId]);

  useEffect(() => { loadUpdates(); }, [loadUpdates]);

  // Determine submitted weeks from history
  const submittedWeeks = new Set(
    updates.filter(u => u.status === "submitted").map(u => u.week_start_date)
  );

  // All past Saturdays (last 8)
  const pastSaturdays = getPastSaturdays(8);

  // Missed Saturdays = past Saturdays that are NOT in submitted weeks
  const missedSaturdays = pastSaturdays.filter(sat => !submittedWeeks.has(sat));

  const isAlreadySubmitted = submittedWeeks.has(activeWeekStart);

  // Form is enabled if:
  // 1. today is Saturday and active week is current Saturday, OR
  // 2. missed week mode is active (a past Saturday is selected)
  const canFill = (isMissedWeekMode || todayIsSaturday) && !isAlreadySubmitted;

  const handleFileChange = (field) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFiles((prev) => ({ ...prev, [field]: file }));
    setPreviews((prev) => ({ ...prev, [field]: URL.createObjectURL(file) }));
  };

  const handleAddPoint = () => setComments(prev => [...prev, ""]);
  const handleRemovePoint = (i) => setComments(prev => prev.filter((_, idx) => idx !== i));
  const handleCommentChange = (i, val) => {
    setComments(prev => { const next = [...prev]; next[i] = val; return next; });
  };

  const resetForm = () => {
    setComments([""]);
    setFiles({ interior_photo_1: null, interior_photo_2: null, exterior_photo_1: null, exterior_photo_2: null });
    setPreviews({});
  };

  const selectMissedWeek = (sat) => {
    setActiveWeekStart(sat);
    setIsMissedWeekMode(true);
    setShowMissedWeekDropdown(false);
    resetForm();
  };

  const switchToCurrentWeek = () => {
    setActiveWeekStart(currentSaturday);
    setIsMissedWeekMode(false);
    resetForm();
  };

  const handleSaveAndSubmit = async () => {
    if (!canFill) {
      toast.error("Weekly updates can only be filled on Saturdays.");
      return;
    }
    if (isAlreadySubmitted) {
      toast.error("This week is already submitted.");
      return;
    }
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("project_id", projectId);
      formData.append("week_start_date", activeWeekStart);
      comments.forEach((c, i) => {
        if (c.trim()) formData.append(`comment_${i + 1}`, c.trim());
      });
      Object.entries(files).forEach(([key, file]) => {
        if (file) formData.append(key, file);
      });

      const createRes = await axiosInstancev1.post("/erp/weekly-updates", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const createdId = createRes?.data?.data?.id;
      if (!createdId) throw new Error("Failed to fetch created weekly update id");

      await axiosInstancev1.post(`/erp/weekly-updates/${createdId}/submit`);
      toast.success("Weekly update saved and submitted!");
      resetForm();
      if (isMissedWeekMode) {
        setIsMissedWeekMode(false);
        setActiveWeekStart(currentSaturday);
      }
      loadUpdates();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Failed to save and submit");
    }
    setSaving(false);
  };

  const activeWeekRange = getWeekRange(activeWeekStart);

  return (
    <div className="space-y-6">
      {/* Saturday only banner when not Saturday */}
      {!todayIsSaturday && !isMissedWeekMode && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <AlertCircle size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-amber-800">Weekly Updates — Saturdays Only</p>
            <p className="text-xs text-amber-700 mt-0.5 font-medium">
              You can only fill in the weekly update form on <strong>Saturdays</strong>. Today is not a Saturday.
            </p>
          </div>
        </div>
      )}

      {/* Missed Weeks Section */}
      {missedSaturdays.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b bg-orange-50/60 flex items-center gap-2">
            <Calendar size={15} className="text-orange-500" />
            <span className="font-bold text-orange-700 text-sm">Missed Weeks</span>
            <span className="ml-1 bg-orange-100 text-orange-600 text-[9px] font-black px-1.5 py-0.5 rounded-full">{missedSaturdays.length}</span>
          </div>
          <div className="px-4 py-3 flex flex-wrap gap-3 items-center">
            <p className="text-xs text-gray-500 font-semibold">Select a missed week to fill:</p>
            <div className="relative min-w-[200px]">
              <select
                value={isMissedWeekMode ? activeWeekStart : ""}
                onChange={e => {
                  if (e.target.value === "") {
                    switchToCurrentWeek();
                  } else {
                    selectMissedWeek(e.target.value);
                  }
                }}
                className="w-full appearance-none border border-orange-200 bg-orange-50/50 hover:bg-orange-50 text-orange-800 rounded-lg pl-3 pr-8 py-2 text-xs font-bold outline-none focus:border-orange-400 transition-colors cursor-pointer"
              >
                <option value="" className="text-gray-500 bg-white">-- Select missed date --</option>
                {missedSaturdays.map(sat => {
                  const r = getWeekRange(sat);
                  return (
                    <option key={sat} value={sat} className="bg-white text-gray-800">
                      {new Date(sat).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })} (Week of {r.start})
                    </option>
                  );
                })}
              </select>
              <div className="absolute right-2.5 top-1/2 -translate-y-1/2 text-orange-600 pointer-events-none">
                <ChevronDown size={14} />
              </div>
            </div>
            {isMissedWeekMode && (
              <button
                onClick={switchToCurrentWeek}
                className="px-3 py-1.5 rounded-lg text-xs font-bold border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all flex items-center gap-1"
              >
                ✕ Cancel
              </button>
            )}
          </div>
        </div>
      )}

      {/* Create form */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b bg-blue-50/50 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <ImageIcon size={18} className="text-blue-600" />
            <div>
              <h3 className="font-bold text-gray-800 text-sm">
                {isMissedWeekMode ? "Fill Missed Weekly Update" : "Create Weekly Update"}
              </h3>
              <p className="text-xs text-gray-500">
                Week: <span className="font-semibold text-gray-700">{activeWeekRange.start}</span> to{" "}
                <span className="font-semibold text-gray-700">{activeWeekRange.end}</span>
                {isMissedWeekMode && (
                  <span className="ml-2 text-[10px] font-bold text-orange-500 bg-orange-50 border border-orange-200 px-1.5 py-0.5 rounded">Missed Week</span>
                )}
              </p>
            </div>
          </div>
          {isAlreadySubmitted && (
            <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-lg">
              ✓ Already Submitted
            </span>
          )}
        </div>

        <div className={`p-4 space-y-5 ${!canFill ? "opacity-60 pointer-events-none select-none" : ""}`}>
          {/* Interior Photos */}
          <div>
            <p className="text-xs font-bold text-gray-600 mb-2 flex items-center gap-1">
              <Home size={14} className="text-orange-500" /> Interior Photos (2 required to submit)
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <PhotoSlot label="Interior Photo 1" preview={previews.interior_photo_1} onChange={handleFileChange("interior_photo_1")} disabled={!canFill} />
              <PhotoSlot label="Interior Photo 2" preview={previews.interior_photo_2} onChange={handleFileChange("interior_photo_2")} disabled={!canFill} />
            </div>
          </div>

          {/* Exterior Photos */}
          <div>
            <p className="text-xs font-bold text-gray-600 mb-2 flex items-center gap-1">
              <Building2 size={14} className="text-emerald-500" /> Exterior Photos (2 required to submit)
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <PhotoSlot label="Exterior Photo 1" preview={previews.exterior_photo_1} onChange={handleFileChange("exterior_photo_1")} disabled={!canFill} />
              <PhotoSlot label="Exterior Photo 2" preview={previews.exterior_photo_2} onChange={handleFileChange("exterior_photo_2")} disabled={!canFill} />
            </div>
          </div>

          {/* Progress Points — dynamic */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold text-gray-600 flex items-center gap-1">
                <MessageSquare size={14} /> Progress Points
              </p>
              <button
                type="button"
                onClick={handleAddPoint}
                disabled={!canFill}
                className="flex items-center gap-1 text-[11px] font-bold text-blue-600 bg-blue-50 border border-blue-100 hover:bg-blue-100 px-2 py-1 rounded-lg transition-colors disabled:opacity-50"
              >
                <Plus size={12} /> Add Point
              </button>
            </div>
            <div className="space-y-2 max-w-xl">
              {comments.map((c, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <span className="text-xs font-bold text-blue-500 w-5 shrink-0">{i + 1}.</span>
                  <input
                    type="text"
                    value={c}
                    disabled={!canFill}
                    onChange={(e) => handleCommentChange(i, e.target.value)}
                    placeholder={`Progress point ${i + 1}`}
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-blue-500 outline-none transition-colors"
                  />
                  {comments.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemovePoint(i)}
                      className="text-red-400 hover:text-red-600 p-1 rounded transition-colors flex-shrink-0"
                      title="Remove point"
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Submit button */}
          <div className="flex gap-2 pt-2">
            <button
              onClick={handleSaveAndSubmit}
              disabled={saving || !canFill || isAlreadySubmitted}
              className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
            >
              <Send size={16} /> {saving ? "Saving..." : "Save & Submit"}
            </button>
            {!canFill && !isAlreadySubmitted && (
              <p className="text-xs text-gray-500 font-medium flex items-center gap-1.5">
                <AlertCircle size={13} className="text-amber-500" />
                {isMissedWeekMode ? "This week is not a missed week or already submitted." : "Available only on Saturdays or select a missed week above."}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* History */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-gray-800 text-sm">Weekly Update History</h3>
          <button
            onClick={loadUpdates}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 font-semibold"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {loading ? (
          <div className="py-12 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-200 border-t-blue-600" />
          </div>
        ) : updates.length === 0 ? (
          <div className="bg-white rounded-xl border border-dashed border-gray-200 p-8 text-center text-gray-400 text-sm">
            No weekly updates yet. Create one above.
          </div>
        ) : (
          <WeeklyUpdateSidebarView
            updates={updates}
            selectedUpdateId={selectedUpdateId}
            onSelectUpdate={setSelectedUpdateId}
            accent="blue"
            emptyMessage="No weekly updates yet. Create one above."
          />
        )}
      </div>
    </div>
  );
};

export default WeeklyUpdateInchargePanel;
